'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/core/presentation/components/Toast';
import {
  LEGAL_DOCUMENT_DEFAULT_PRIVACY,
  LEGAL_DOCUMENT_DEFAULT_TERMS,
} from '@/core/presentation/constants/legal-document-default-content';
import { firebaseAuthService, RecaptchaVerifier } from '@/core/services';
import { formatE164ToDisplay } from '@/core/utils/formatPhone';
import { useAuth } from '@/features/authentication/presentation/context/AuthContext';
import type { OnboardingFormProps } from '@/features/authentication/presentation/components/OnboardingForm';
import type { OnboardingFormSubmitPayload } from '@/features/authentication/data/types/onboarding-form.types';
import { AUTH_PROVIDER_ID } from '@/features/authentication/presentation/composables/constants/auth.constants';
import {
  ONBOARDING_BRIDGE,
  ONBOARDING_BRIDGE_COPY,
  ONBOARDING_PAGE_COPY,
  ONBOARDING_PHONE_VERIFY_CODE_RESENT_TOAST,
  ONBOARDING_PHONE_VERIFY_CODE_SENT_TOAST,
  ONBOARDING_PHONE_VERIFY_SUCCESS,
} from '@/features/authentication/presentation/composables/constants/onboarding.constants';
import { ROUTES } from '@/app/routes';
import { PRODUCT_DEMO_STEPS, userSchema, type User as DomainUser } from '@/core/domain';
import { useUser } from '@/features/app/user/presentation/context/UserContext';

/** Login Google/Meta: e-mail e nome vêm do provedor; campos só leitura no onboarding. */
function hasSocialOAuthProvider(user: FirebaseUser | null | undefined): boolean {
  if (!user?.providerData?.length) return false;
  return user.providerData.some(
    (p) => p.providerId === AUTH_PROVIDER_ID.GOOGLE || p.providerId === AUTH_PROVIDER_ID.FACEBOOK
  );
}

export type OnboardingBridgeProps = {
  layout: {
    title: string;
    subtitle: string;
  };
  exitFooter: {
    loading: boolean;
    loadingLabel: string;
    label: string;
    onExit: () => void | Promise<void>;
  };
  form: OnboardingFormProps;
};

/**
 * Ponte entre `AuthContext` e a tela de onboarding (`OnboardingForm` + rodapé de saída).
 * Handlers do formulário mínimos por enquanto; vamos evoluindo um a um.
 */
export function useOnboardingBridge(): OnboardingBridgeProps {
  const navigate = useNavigate();
  const toast = useToast();
  const { state: authState, events } = useAuth();
  const { loading: authLoading, error, user } = authState;
  const { events: userEvents } = useUser();

  const [exitLoading, setExitLoading] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const recaptchaVerifierRef = useRef<InstanceType<typeof RecaptchaVerifier> | null>(null);

  useEffect(() => {
    return () => {
      try {
        recaptchaVerifierRef.current?.clear();
      } catch {
        // noop: widget / DOM pode já estar inválido no unmount
      }
      recaptchaVerifierRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!error) return;
    toast.error(error);
    events.clearError();
  }, [error, toast, events]);

  const getOrCreateRecaptchaVerifier = useCallback(() => {
    if (recaptchaVerifierRef.current) return recaptchaVerifierRef.current;
    const auth = firebaseAuthService.getAuth();
    recaptchaVerifierRef.current = new RecaptchaVerifier(
      auth,
      ONBOARDING_BRIDGE.RECAPTCHA_CONTAINER_ID,
      { size: ONBOARDING_BRIDGE.RECAPTCHA_SIZE }
    );
    return recaptchaVerifierRef.current;
  }, []);

  const handleExitOnboarding = useCallback(async () => {
    setExitLoading(true);
    try {
      await events.logout();
      navigate(ROUTES.LOGIN, { replace: true });
    } finally {
      setExitLoading(false);
    }
  }, [events, navigate]);

  const onWhatsAppChange = useCallback(
    (_nationalParsed: string) => {
      events.clearError();
    },
    [events]
  );

  const onRequestSendPhoneCode = useCallback(
    async (whatsappNationalDigits: string) => {
      if (whatsappNationalDigits.length < ONBOARDING_BRIDGE.MIN_NATIONAL_DIGITS) return false;
      const e164 = `${ONBOARDING_BRIDGE.E164_BR_PREFIX}${whatsappNationalDigits}`;
      const verifier = getOrCreateRecaptchaVerifier();
      const sent = await events.sendOtpForPhoneLink(e164, verifier);
      if (sent) toast.success(ONBOARDING_PHONE_VERIFY_CODE_SENT_TOAST);
      return sent;
    },
    [events, getOrCreateRecaptchaVerifier, toast]
  );

  const onResendPhoneCode = useCallback(
    async (whatsappNationalDigits: string) => {
      if (whatsappNationalDigits.length < ONBOARDING_BRIDGE.MIN_NATIONAL_DIGITS) return false;
      const e164 = `${ONBOARDING_BRIDGE.E164_BR_PREFIX}${whatsappNationalDigits}`;
      const verifier = getOrCreateRecaptchaVerifier();
      const sent = await events.resendOtpForPhoneLink(e164, verifier);
      if (sent) toast.success(ONBOARDING_PHONE_VERIFY_CODE_RESENT_TOAST);
      return sent;
    },
    [events, getOrCreateRecaptchaVerifier, toast]
  );

  const onConfirmLinkCode = useCallback(
    async (code: string) => {
      const ok = await events.linkPhoneToAccount(code);
      if (ok) toast.success(ONBOARDING_PHONE_VERIFY_SUCCESS);
      return ok;
    },
    [events, toast]
  );

  const onChangePhoneNumberStep = useCallback(() => {
    events.clearError();
  }, [events]);

  const onSubmit = useCallback(
    async (payload: OnboardingFormSubmitPayload) => {
      const uid = user?.uid;
      if (!uid) {
        toast.error(ONBOARDING_BRIDGE_COPY.REQUIRE_LOGIN_TO_CONTINUE);
        return;
      }
      setSubmitBusy(true);
      try {
        const existing = await userEvents.getUserById(uid, { forceRefresh: true });
        const now = new Date().toISOString();
        const phone = `${ONBOARDING_BRIDGE.E164_BR_PREFIX}${payload.whatsappNationalDigits}`;
        const profilePatch: Pick<
          DomainUser,
          | 'phone'
          | 'phone_verified'
          | 'email'
          | 'name'
          | 'company_name'
          | 'accepted_terms_at'
          | 'accepted_privacy_at'
          | 'updated_at'
        > = {
          phone,
          phone_verified: payload.phoneVerified,
          email: payload.email,
          name: payload.name,
          company_name: payload.companyName,
          accepted_terms_at: payload.acceptedTerms ? now : null,
          accepted_privacy_at: payload.acceptedPrivacy ? now : null,
          updated_at: now,
        };

        if (existing) {
          const updated = await userEvents.updateUser(uid, profilePatch);
          if (!updated) {
            toast.error(ONBOARDING_BRIDGE_COPY.PROFILE_CREATED_FAILURE);
            return;
          }
        } else {
          /** Primeira criação do doc: inicia demo guiada na etapa de configurações de lembretes (periodicidade/hora). */
          const newUser = userSchema.parse({
            id: uid,
            ...profilePatch,
            created_at: now,
            product_demo_started_at: now,
            product_demo_last_step: PRODUCT_DEMO_STEPS[0],
          });
          const { user: created, error: createError } = await userEvents.createUser(newUser);
          if (createError || !created) {
            toast.error(ONBOARDING_BRIDGE_COPY.PROFILE_CREATED_FAILURE);
            return;
          }
        }

        toast.success(ONBOARDING_BRIDGE_COPY.PROFILE_CREATED_SUCCESS);
        navigate(ROUTES.CHOOSE_PLAN, { replace: true });
      } catch {
        toast.error(ONBOARDING_BRIDGE_COPY.PROFILE_CREATED_FAILURE);
      } finally {
        setSubmitBusy(false);
      }
    },
    [user?.uid, userEvents, toast, navigate]
  );

  const onValidationError = useCallback(
    (message: string) => {
      toast.error(message);
    },
    [toast]
  );

  const form = useMemo(
    (): OnboardingFormProps => ({
      initialWhatsApp: formatE164ToDisplay(user?.phoneNumber ?? undefined),
      initialEmail: firebaseAuthService.resolveAccountEmail(user) ?? user?.email ?? '',
      initialName: user?.displayName ?? '',
      /** Login por SMS: número já verificado no Firebase — não pedir OTP de novo. */
      phoneVerified: Boolean(user?.phoneNumber),
      emailReadOnly: hasSocialOAuthProvider(user),
      nameReadOnly: hasSocialOAuthProvider(user),
      emailDraftHint: firebaseAuthService.resolveAccountEmail(user) ?? user?.email ?? '',
      authLoading,
      submitLoading: submitBusy,
      onWhatsAppChange,
      onRequestSendPhoneCode,
      onResendPhoneCode,
      onConfirmLinkCode,
      onChangePhoneNumberStep,
      onSubmit,
      onValidationError,
      privacyModalBody: LEGAL_DOCUMENT_DEFAULT_PRIVACY,
      termsModalBody: LEGAL_DOCUMENT_DEFAULT_TERMS,
    }),
    [
      user,
      authLoading,
      submitBusy,
      onWhatsAppChange,
      onRequestSendPhoneCode,
      onResendPhoneCode,
      onConfirmLinkCode,
      onChangePhoneNumberStep,
      onSubmit,
      onValidationError,
    ]
  );

  return useMemo(
    () => ({
      layout: {
        title: ONBOARDING_PAGE_COPY.TITLE,
        subtitle: ONBOARDING_PAGE_COPY.SUBTITLE,
      },
      exitFooter: {
        loading: exitLoading,
        loadingLabel: ONBOARDING_PAGE_COPY.EXIT_LOADING_LABEL,
        label: ONBOARDING_PAGE_COPY.EXIT_FOOTER_LABEL,
        onExit: handleExitOnboarding,
      },
      form,
    }),
    [exitLoading, form, handleExitOnboarding]
  );
}
